/**
 * Regras de apresentação do Balancete contábil.
 *
 * Decisões de negócio que NÃO podem mudar (validadas com o contador):
 *  - A ordem do plano de contas NUNCA é alfabética: é a ordem contábil, por
 *    `classificacao`. O backend já devolve `ORDER BY classificacao`; aqui só
 *    preservamos essa ordem ao montar a hierarquia.
 *  - Hierarquia sintético (`tipo = 'S'`, agrupador) → analítico (`tipo = 'A'`,
 *    folha onde há movimento). Só analítica abre a composição do saldo.
 *  - Natureza do saldo sempre explícita (`656,00 C`), em todos os níveis.
 *  - `—` (sem valor) é visualmente diferente de `0,00` (zero real).
 *  - O saldo atual é `saldo anterior + débito − crédito`, NUNCA só o movimento
 *    do período — ver `marcaDoSaldo` para o que a tela faz quando a abertura
 *    não existe ou quando as duas fontes discordam.
 */

import { IBalanceteCargas, IContabilBalancete } from '../../auditoriaInteligente/types';

/** Natureza contábil do saldo: Devedor, Credor ou zero. */
export type Natureza = 'D' | 'C' | null;

/**
 * Formata valor distinguindo "sem valor" (`—`) de "zero real" (`0,00`).
 *
 * SEM O `R$` (pedido do usuário, 23/09/2026). O balancete impresso do Domínio
 * não traz o símbolo em coluna nenhuma, e a tela é conferida lado a lado com
 * ele: repetir "R$" em quatro colunas e centenas de linhas gasta largura —
 * justamente a largura que faltava para "Fornecedor" e "Histórico" — sem
 * acrescentar informação, porque a moeda é a mesma na tabela inteira.
 *
 * Duas casas continuam obrigatórias: sem elas `1.738.591,3` e `1.738.591,30`
 * ficariam visualmente diferentes na mesma coluna, e o contador confere
 * alinhando os centavos.
 *
 * COERCAO DEFENSIVA — não remover (bug real em produção, 15/09/2026):
 * `String.prototype.toLocaleString()` IGNORA as opções e devolve os dígitos crus
 * (`"110000.00"` -> `110000.00`). Quando o JSON chega com os numéricos como
 * STRING, as colunas Débito e Crédito viravam uma fileira de números grudados,
 * sem separador, que estourava a largura da tela — enquanto a coluna Saldo
 * continuava certa, porque `fmtSaldo` passa por `Math.abs()` e isso já coage
 * para número. Era exatamente essa assimetria que aparecia na tela.
 *
 * `Number(...)` resolve na origem: o valor é coagido ANTES de formatar, então
 * não importa se a API mandou number ou string. Valor não numérico (texto
 * qualquer) vira `—` em vez de `NaN`, que não significaria nada pro contador.
 */
export const fmtMoney = (n: number | string | null | undefined): string => {
  if (n == null || n === '') return '—';
  const valor = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(valor)) return '—';
  return valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Natureza do saldo. Positivo = devedor (D), negativo = credor (C).
 * Saldo zero não tem natureza — não faz sentido dizer "0,00 D".
 */
export const naturezaDe = (saldo: number | null | undefined): Natureza => {
  if (saldo == null || saldo === 0) return null;
  return saldo > 0 ? 'D' : 'C';
};

/**
 * Como a natureza aparece nos TEXTOS da tela — o selo do balancete e o
 * detalhe do card: `Deb` e `Cred` por extenso (pedido do usuário,
 * 30/09/2026).
 *
 * NA TABELA a natureza continua sendo a LETRA (`62.202.991,65 D`): é o
 * formato do balancete impresso do Domínio e a coluna é lida em série, onde
 * a palavra inteira vira ruído. Por extenso só onde é frase (`Deb = Cred`),
 * que é onde uma letra solta fica críptica.
 *
 * O CONTRATO não muda: `Natureza` continua `'D' | 'C'` — é o que o backend
 * manda (`saldo_razao_natureza`) e o que o Excel exportado usa.
 */
export const ROTULO_NATUREZA: Record<'D' | 'C', string> = {
  D: 'Deb',
  C: 'Cred',
};

/**
 * Saldo no padrão aprovado da tela: valor ABSOLUTO + letra da natureza
 * (`656,00 C`). O sinal vira letra — é assim que o contador lê balancete, e é
 * o formato do PDF do Domínio (`407,41D`).
 * Sem valor continua `—`; zero real vira `0,00` sem letra.
 */
export const fmtSaldo = (saldo: number | null | undefined): string => {
  if (saldo == null) return '—';
  const nat = naturezaDe(saldo);
  const base = fmtMoney(Math.abs(saldo));
  return nat ? `${base} ${nat}` : base;
};

/** Nó da árvore do balancete: a conta + filhos + profundidade de indentação. */
export interface NoBalancete extends IContabilBalancete {
  children: NoBalancete[];
  depth: number;
}

/**
 * Chave estável de uma conta: classificação + CÓDIGO.
 *
 * A classificação NÃO é única. Medido em produção: 7.550 pares
 * (empresa, classificação) têm mais de uma conta, e na 856 há uma com CINCO
 * (`512160003`). Casos reais dessa empresa: `211030003` (IRRF a Pagar),
 * `231090001` (Lucros Acumulados), `512160003` (Despesa Telefone e Despesa
 * Serviços Prestados por Terceiros) e `515010007`.
 *
 * Com a classificação sozinha, duas contas diferentes viravam a MESMA chave no
 * `Set` de recolhidos: recolher/expandir uma mexia na outra, e o estado
 * inicial (tudo recolhido) não conseguia fechar as duas — elas apareciam soltas
 * entre os grupos de 1º nível, que foi o defeito relatado na validação.
 *
 * O código da conta desempata (é a PK do plano no Domínio).
 */
export const chaveDe = (c: IContabilBalancete): string =>
  `${c.classificacao ?? ''}#${c.codigo_conta ?? ''}`;

/**
 * Monta a árvore a partir da lista flat JÁ ORDENADA por classificação.
 * O filho começa com a classificação do pai ('1' → '1.01' → '1.01.001'), então
 * uma pilha de ancestrais resolve a hierarquia em uma passada — sem reordenar
 * nada (reordenar quebraria a ordem contábil).
 */
export function montarArvore(contas: IContabilBalancete[]): NoBalancete[] {
  const roots: NoBalancete[] = [];
  const stack: NoBalancete[] = [];
  contas.forEach(c => {
    const no: NoBalancete = { ...c, children: [], depth: 0 };
    const cl = c.classificacao ?? '';
    while (stack.length) {
      const topo = stack[stack.length - 1].classificacao ?? '';
      if (cl.startsWith(topo) && cl.length > topo.length) break;
      stack.pop();
    }
    if (stack.length) {
      const pai = stack[stack.length - 1];
      no.depth = pai.depth + 1;
      pai.children.push(no);
    } else {
      roots.push(no);
    }
    stack.push(no);
  });
  return roots;
}

/**
 * Achata a árvore em linhas visíveis, respeitando os nós recolhidos.
 * `forcarAberto` é usado durante a busca — filtrar e manter tudo fechado
 * esconderia justamente o resultado que o usuário procurou.
 */
export function achatar(
  nodes: NoBalancete[],
  recolhidos: Set<string>,
  forcarAberto: boolean,
): NoBalancete[] {
  const out: NoBalancete[] = [];
  const walk = (lista: NoBalancete[]) => {
    lista.forEach(n => {
      out.push(n);
      const aberto = forcarAberto || !recolhidos.has(chaveDe(n));
      if (aberto && n.children.length) walk(n.children);
    });
  };
  walk(nodes);
  return out;
}

/**
 * Filtra por texto (código, nome ou classificação) mantendo ancestrais E
 * descendentes do match — buscar um grupo sintético tem que mostrar o que
 * tem dentro, e buscar uma analítica tem que mostrar onde ela mora.
 */
export function filtrarContas(contas: IContabilBalancete[], busca: string): IContabilBalancete[] {
  const q = busca.trim().toLowerCase();
  if (!q) return contas;
  const bate = (c: IContabilBalancete) =>
    (c.codigo_conta ?? '').toLowerCase().includes(q) ||
    (c.nome_conta ?? '').toLowerCase().includes(q) ||
    (c.classificacao ?? '').toLowerCase().includes(q);

  const manter = new Set<string>();
  contas.forEach(c => {
    if (!bate(c)) return;
    const cl = c.classificacao ?? '';
    manter.add(chaveDe(c));
    if (!cl) return;
    contas.forEach(a => {
      const acl = a.classificacao ?? '';
      if (acl && (cl.startsWith(acl) || acl.startsWith(cl))) manter.add(chaveDe(a));
    });
  });
  return contas.filter(c => manter.has(chaveDe(c)));
}

/**
 * A linha tem conta RECÍPROCA entre empresas do grupo?
 *
 * Recíproca = conta que representa uma empresa do grupo dentro da outra
 * (conta corrente filial, transferência de estoque entre matriz e filial).
 * A ITG 2000 (R1), item 24, manda ELIMINÁ-LAS na elaboração das
 * demonstrações; a soma simples que a tela faz NÃO elimina. Por isso o
 * contador precisa achar essas linhas antes de usar o total somado.
 *
 * Só o balancete consolidado traz `reciprocas` — no de uma empresa só não há
 * recíproca a eliminar, e o botão nem aparece.
 */
export const temReciproca = (c: IContabilBalancete): boolean => (c.reciprocas?.length ?? 0) > 0;

/**
 * Recorta a árvore nas linhas com conta recíproca, mantendo os ANCESTRAIS.
 *
 * Sem os ancestrais a conta apareceria solta, fora do grupo que a contém, e
 * `montarArvore` perderia a hierarquia (ela depende de o pai estar na lista).
 *
 * Diferente de `filtrarContas`, aqui NÃO trazemos os descendentes: quem filtra
 * quer ver as recíprocas, não tudo que mora no grupo delas.
 */
export function filtrarReciprocas(contas: IContabilBalancete[]): IContabilBalancete[] {
  const manter = new Set<string>();
  contas.forEach(c => {
    if (!temReciproca(c)) return;
    manter.add(chaveDe(c));
    const cl = c.classificacao ?? '';
    if (!cl) return;
    contas.forEach(a => {
      const acl = a.classificacao ?? '';
      if (acl && cl.startsWith(acl)) manter.add(chaveDe(a));
    });
  });
  return contas.filter(c => manter.has(chaveDe(c)));
}

/**
 * Quantas linhas ANALÍTICAS têm recíproca — o número que o botão exibe.
 * As sintéticas herdam a evidência das filhas, então contá-las inflaria o
 * total: o mesmo documento apareceria uma vez por nível da árvore.
 */
export const contarReciprocas = (contas: IContabilBalancete[]): number =>
  contas.filter(c => c.tipo !== 'S' && temReciproca(c)).length;

/** Linha com saldo contrário à natureza da conta (o selo âmbar da árvore). */
export const temSaldoInvertido = (c: IContabilBalancete): boolean => !!c.alerta;

/**
 * Recorta a árvore nas linhas com saldo invertido, mantendo os ANCESTRAIS.
 *
 * Mesma mecânica de `filtrarReciprocas` (ancestral sim, descendente não):
 * quem filtra quer ver as invertidas, não tudo que mora no grupo delas. Sem
 * os ancestrais `montarArvore` perderia a hierarquia, porque ela depende de
 * o pai estar na lista.
 *
 * Pedido da operação (23/10/2026): a tela dizia QUANTAS eram e o contador
 * tinha de abrir conta por conta para achar QUAIS — "tenho que abrir conta
 * por conta?".
 */
export function filtrarInvertidas(contas: IContabilBalancete[]): IContabilBalancete[] {
  const manter = new Set<string>();
  contas.forEach(c => {
    if (!temSaldoInvertido(c)) return;
    manter.add(chaveDe(c));
    const cl = c.classificacao ?? '';
    if (!cl) return;
    contas.forEach(a => {
      const acl = a.classificacao ?? '';
      if (acl && cl.startsWith(acl)) manter.add(chaveDe(a));
    });
  });
  return contas.filter(c => manter.has(chaveDe(c)));
}

/**
 * Quantas linhas ANALÍTICAS estão invertidas — o número que o filtro exibe.
 *
 * Só analítica, pelo mesmo motivo de `contarReciprocas`: a sintética herda o
 * alerta das filhas (regra do backend), então contá-la somaria a mesma conta
 * uma vez por nível da árvore. Medido na 856: 48 analíticas marcadas.
 */
export const contarInvertidas = (contas: IContabilBalancete[]): number =>
  contas.filter(c => c.tipo !== 'S' && temSaldoInvertido(c)).length;

/**
 * Texto da evidência: QUAL conta, de QUAL empresa, com que saldo.
 * Antes a tela dizia só "4 contas recíprocas" e o contador não sabia onde
 * conferir — que é justamente o que ele precisa para eliminar.
 */
/** Quebra de linha do tooltip: o atributo `title` do HTML respeita LF. */
const NL = String.fromCharCode(10);

export const descreverReciprocas = (c: IContabilBalancete): string => {
  const rs = c.reciprocas ?? [];
  if (!rs.length) return '';
  const linhas = rs.map(
    r =>
      `empresa ${r.external_id} · conta ${r.codigo_conta ?? '—'} ${
        r.nome_conta ?? '—'
      } · saldo ${fmtMoney(r.saldo ?? 0)}`,
  );
  const cabecalho =
    'Conta recíproca entre empresas do grupo (ITG 2000 R1, item 24: deve ser ' +
    'eliminada no consolidado — a soma desta tela não elimina):';
  return `${cabecalho}${NL}${linhas.join(NL)}`;
};

/** Resumo do estado da base, no espírito do header da tela aprovada. */
export interface ResumoBalancete {
  totalContas: number;
  analiticas: number;
  sinteticas: number;
  /** Soma dos débitos das contas ANALÍTICAS (as folhas de movimento). */
  totalDebito: number;
  /** Soma dos créditos das contas ANALÍTICAS. */
  totalCredito: number;
  /** Diferença débito − crédito das analíticas. */
  diferenca: number;
  /** true só quando a partida dobrada foi CONFERIDA como fechada. */
  fecha: boolean;
  alertas: number;
  /**
   * Analíticas cujo saldo (do ERP) NÃO reproduz saldo anterior + débito −
   * crédito (os pontos laranja). É o SEGUNDO eixo do selo: D = C pode fechar
   * com os saldos divergindo — as diferenças se anulam (856, 30/09/2026).
   */
  saldosDivergentes: number;
  /**
   * Tamanho da divergência entre as fontes: Σ|saldo − calculado| / 2. Cada
   * lançamento que uma fonte tem e a outra não aparece uma vez no débito e
   * uma no crédito, então a soma dos módulos conta o mesmo dinheiro duas
   * vezes. Na 856 dá 783.580,49 — exatamente o movimento de setembro que a
   * carga de saldos (29/09) ainda não tinha e a de lançamentos (30/09) tinha.
   */
  divergenciaSaldos: number;
}

/**
 * Totais do balancete a partir das contas ANALÍTICAS — as sintéticas são
 * agrupadoras e já contêm o valor das filhas; somar as duas contaria o mesmo
 * lançamento mais de uma vez.
 *
 * `fecha` afirma a partida dobrada (débitos = créditos, tolerância de 1
 * centavo para arredondamento) e a UI só exibe o selo no caso POSITIVO. Uma
 * diferença aqui não prova erro contábil: pode ser recorte de período ou
 * particularidade da extração. Por isso a tela não acusa "não fecha" — apenas
 * deixa de confirmar, e mostra a diferença como informação neutra para o
 * contador conferir. Acusar erro com base numa premissa não validada contra
 * dado real seria pior do que não afirmar nada.
 */
export function resumirBalancete(contas: IContabilBalancete[]): ResumoBalancete {
  const analiticas = contas.filter(c => c.tipo === 'A');
  // `Number(...)`: o backend chegou a mandar estes totais como STRING
  // ("18898510.71" — Decimal serializado pelo pydantic) e `+` em string
  // concatena: o cabeçalho virava NaN e mostrava "—" enquanto a árvore, que só
  // formata, seguia certa. O backend foi corrigido (response_model=None), mas
  // somar sem coagir é depender de um detalhe de serialização de outro repo.
  const totalDebito = analiticas.reduce((s, c) => s + Number(c.total_debito ?? 0), 0);
  const totalCredito = analiticas.reduce((s, c) => s + Number(c.total_credito ?? 0), 0);
  // Somar dezenas de `numeric(18,2)` em ponto flutuante acumula resíduo: na
  // empresa 856 (79 contas, R$ 18.898.510,71 de cada lado) a subtração dá
  // 7,45e-9 em vez de 0. Arredondamos para centavo — a menor unidade que o
  // balancete representa — antes de comparar e de exibir. Sem isso o selo caía
  // no ramo negativo e mostrava "diferença D−C R$ 0,00", que se contradiz.
  const diferenca = Math.round((totalDebito - totalCredito) * 100) / 100;
  const divergentes = analiticas.filter(c => c.saldo_conferido === false);
  const divergenciaSaldos =
    Math.round(
      divergentes.reduce(
        (s, c) => s + Math.abs(Number(c.saldo ?? 0) - Number(c.saldo_calculado ?? 0)),
        0,
      ) * 50,
    ) / 100;
  return {
    totalContas: contas.length,
    analiticas: analiticas.length,
    sinteticas: contas.length - analiticas.length,
    totalDebito,
    totalCredito,
    diferenca,
    fecha: analiticas.length > 0 && diferenca === 0,
    alertas: contas.filter(c => c.alerta).length,
    saldosDivergentes: divergentes.length,
    divergenciaSaldos,
  };
}

/**
 * Rota do Papel de trabalho da conta (a próxima tela — Composição de Saldo).
 *
 * CONTRATO com a tela de Papel de trabalho (implementada em paralelo):
 *   /modulesHome/accountingConciliation/balancete/conta
 *     ?empresa=<tb_empresa.id>       — id INTERNO (o mesmo do path da API)
 *     &conta=<classificacao>         — classificação contábil da conta analítica
 *     &codigo=<codigo_conta>         — código da conta no Domínio
 *     &data_ini=YYYY-MM-DD           — início do período do balancete
 *     &data_fim=YYYY-MM-DD           — data-posição do saldo (fim do período)
 */
export const papelDeTrabalhoUrl = (params: {
  empresaId: number;
  classificacao: string;
  codigoConta: string;
  dataIni?: string;
  dataFim?: string;
}): string => {
  const p = new URLSearchParams();
  p.set('empresa', String(params.empresaId));
  p.set('conta', params.classificacao);
  p.set('codigo', params.codigoConta);
  if (params.dataIni) p.set('data_ini', params.dataIni);
  if (params.dataFim) p.set('data_fim', params.dataFim);
  return `/modulesHome/accountingConciliation/balancete/conta?${p.toString()}`;
};

/**
 * O que a tela precisa dizer sobre a CONFIABILIDADE de um saldo.
 *
 * O saldo atual é `saldo anterior + débito − crédito` (validado contra o PDF
 * do Domínio, empresa 856 jan/2026, 9 linhas conferidas). Duas coisas podem
 * enfraquecer esse número, e nenhuma delas pode ficar muda:
 *
 *  1. NÃO HÁ ABERTURA: mostramos o movimento do período. O número está certo,
 *     mas não é o saldo atual — e sem aviso o contador lê como se fosse.
 *  2. AS DUAS FONTES DISCORDAM: calculamos um valor e o ERP gravou outro.
 *     Mostramos o NOSSO (decisão do usuário) e marcamos, porque escolher em
 *     silêncio esconderia que existe uma segunda versão.
 *
 * `null` = nada a sinalizar. É o caso normal, e é importante que seja mudo:
 * um ponto em toda linha deixaria de significar qualquer coisa.
 */
export interface IMarcaSaldo {
  /** O caractere exibido em sobrescrito, à direita do valor. */
  marca: string;
  /** Texto do `title` — explica por que o ponto está ali. */
  explicacao: string;
}

export const marcaDoSaldo = (c: {
  saldo_conferido?: boolean | null;
  saldo_sem_abertura?: boolean;
  saldo_calculado?: number | null;
  saldo_anterior?: number | null;
  sintetica?: boolean;
}): IMarcaSaldo | null => {
  // Divergência primeiro: é a mais grave das duas, e acumular marcas numa
  // linha só a tornaria ilegível.
  //
  // QUEM PREVALECE MUDOU (backend #1457): o saldo exibido passou a ser o do
  // ERP — é o número do balancete impresso, e o único que fecha ativo contra
  // passivo. Antes mostrávamos o nosso cálculo, e esta frase dizia isso; se
  // ela tivesse ficado, as 48 linhas marcadas da 856 exibiriam uma explicação
  // afirmando o contrário do que a tela faz.
  if (c.saldo_conferido === false) {
    // GRUPO herda a marca de qualquer filha divergente (regra do backend), e
    // as diferenças podem se compensar: na 856 (30/09/2026) o grupo 6 batia
    // ao centavo com duas filhas marcadas (+70.834 e −70.834). Dizer aqui
    // "pelos lançamentos seria X" com X igual ao saldo exibido seria absurdo.
    if (c.sintetica) {
      return {
        marca: '•',
        explicacao:
          'Alguma conta deste grupo tem saldo do ERP diferente do que os lançamentos produzem. As diferenças podem se compensar no total do grupo — por isso ele pode bater mesmo marcado. Abra o grupo para ver as contas marcadas.',
      };
    }
    const nosso = c.saldo_calculado == null ? null : fmtSaldo(c.saldo_calculado);
    return {
      marca: '•',
      explicacao: nosso
        ? `Saldo registrado pelo ERP. Pelos lançamentos do período o saldo seria ${nosso} — a diferença indica que a carga do ERP não cobre todo o movimento. Confira a conta antes de concluir.`
        : 'Saldo registrado pelo ERP. Ele não bate com o que os lançamentos do período produzem — confira a conta antes de concluir.',
    };
  }
  if (c.saldo_sem_abertura) {
    // DOIS CASOS DIFERENTES, e dizer o texto errado é pior que não marcar.
    //
    // Visto em produção (24/09/2026): o grupo `1 ATIVO` exibia saldo anterior
    // de 36.206.665,74 e o tooltip dizia "esta empresa não tem saldo de
    // abertura carregado". A frase contradizia o número na mesma linha — o
    // contador que confere lê uma das duas como defeito da tela.
    //
    // O grupo herda a marca de QUALQUER filha sem abertura (regra correta: um
    // total incompleto não pode se apresentar como completo), mas ele mesmo
    // tem abertura — só não a de todas as contas que soma.
    const parcial = c.saldo_anterior != null;
    return {
      marca: '•',
      explicacao: parcial
        ? 'Alguma conta deste grupo não tem saldo de abertura carregado, então este total cobre apenas o movimento do período dessas contas — está incompleto.'
        : 'Esta conta não tem saldo de abertura carregado, então o valor mostrado é apenas o movimento do período — não o saldo atual.',
    };
  }
  return null;
};

/**
 * O selo do cabeçalho confere DOIS eixos, e só afirma "fecha" quando os dois
 * passam:
 *
 *  1. PARTIDA DOBRADA — total de débitos = total de créditos nas analíticas.
 *  2. SALDOS — cada saldo (do ERP) reproduz saldo anterior + débito − crédito.
 *
 * O caso que motivou o segundo eixo (856, 30/09/2026): D = C fechava ao
 * centavo e o selo era verde, mas 4 dos 6 grupos tinham saldo ≠ anterior ±
 * movimento — R$ 499.794,97 de cada lado, que se anulavam. A causa: saldos do
 * ERP carregados em 29/09 22:10 (setembro "até 29/09") e lançamentos em 30/09;
 * R$ 783.580,49 de lançamentos de setembro entraram entre as duas cargas.
 * "fecha ✓" nessa situação engana — e o selo âmbar diz quantos saldos
 * divergem, quanto e POR QUÊ (as datas das cargas), em vez de só acusar.
 */
export type TomSelo = 'ok' | 'atencao' | 'neutro';

export interface ISeloBalancete {
  tom: TomSelo;
  rotulo: string;
  explicacao: string;
}

/** Cores por tom — verde só no caso POSITIVO dos dois eixos. */
export const COR_SELO: Record<TomSelo, { bg: string; fg: string }> = {
  ok: { bg: 'rgba(22, 163, 74, 0.10)', fg: '#16A34A' },
  atencao: { bg: 'rgba(217, 119, 6, 0.10)', fg: '#D97706' },
  neutro: { bg: 'rgba(15, 23, 42, 0.06)', fg: '#64748B' },
};

/** `dd/mm/aaaa` de uma data ISO; "—" sem valor. */
export const fmtDia = (iso: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

/**
 * `dd/mm/aaaa hh:mm` em horário de Brasília; "—" sem valor. Fuso explícito
 * porque as cargas vêm em UTC e a tela é lida no Brasil — "22:10" é o que o
 * contador confere contra o horário do job.
 */
export const fmtDataHora = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d
    .toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    .replace(',', '');
};

export const seloDoBalancete = (
  r: ResumoBalancete,
  cargas?: IBalanceteCargas | null,
): ISeloBalancete => {
  if (!r.fecha) {
    return {
      tom: 'neutro',
      rotulo: `diferença ${ROTULO_NATUREZA.D}−${ROTULO_NATUREZA.C} ${fmtMoney(r.diferenca)}`,
      explicacao:
        'Débitos e créditos das analíticas não batem neste recorte — conferir antes de concluir',
    };
  }
  if (r.saldosDivergentes === 0) {
    return {
      tom: 'ok',
      rotulo: 'fecha ✓',
      explicacao:
        'Partida dobrada conferida (total de débitos = total de créditos nas contas analíticas) e todos os saldos reproduzem saldo anterior + débito − crédito.',
    };
  }
  const n = r.saldosDivergentes;
  const temCargas = !!(cargas && (cargas.saldos_carregados_em || cargas.lancamentos_carregados_em));
  const porQue = temCargas
    ? ` Os saldos vêm da carga do ERP de ${fmtDataHora(cargas?.saldos_carregados_em)}${
        cargas?.saldos_ate ? ` (até ${fmtDia(cargas.saldos_ate)})` : ''
      }; os débitos e créditos, da carga de lançamentos de ${fmtDataHora(
        cargas?.lancamentos_carregados_em,
      )}. Volta a bater quando as duas cargas cobrirem o mesmo movimento.`
    : '';
  return {
    tom: 'atencao',
    rotulo: `${ROTULO_NATUREZA.D} = ${ROTULO_NATUREZA.C} · ${
      n === 1 ? '1 saldo' : `${n} saldos`
    } ≠ ERP`,
    explicacao: `Débitos e créditos das analíticas se igualam, mas ${
      n === 1 ? '1 saldo não reproduz' : `${n} saldos não reproduzem`
    } saldo anterior + débito − crédito: cerca de ${fmtMoney(
      r.divergenciaSaldos,
    )} de lançamentos que uma fonte tem e a outra não.${porQue} Os pontos laranja marcam as contas.`,
  };
};

/**
 * O código da conta para abrir o papel de trabalho.
 *
 * No balancete de UMA empresa, `codigo_conta` vem preenchido e é esse. No
 * balancete SOMADO não: a mesma conta tem código diferente em cada empresa
 * (o plano do Domínio é por empresa), então a linha consolidada traz
 * `codigo_conta: null` e um mapa `codigos` com o código de cada uma.
 *
 * Medido em 05/10/2026 na 856 + 970: **todas as 175 analíticas** do
 * consolidado vinham sem `codigo_conta`. A URL do papel saía com
 * `codigo=null` e a tela abria vazia, com "null — 2026" no cabeçalho.
 *
 * `externalId` é a empresa cujo papel vamos abrir — normalmente a que está
 * aberta na tela. Sem código para ela, devolve `null` e o chamador não navega:
 * abrir o papel errado é pior que não abrir.
 */
export const codigoParaPapel = (
  conta: IContabilBalancete,
  externalId: number | null | undefined,
): string | null => {
  if (conta.codigo_conta) return conta.codigo_conta;
  const mapa = conta.codigos;
  if (!mapa || externalId == null) return null;
  return mapa[String(externalId)] ?? null;
};
